// src/pages/account/PhoneField.tsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";

import PhoneInput from "react-phone-number-input";
import type { E164Number } from "libphonenumber-js";
import "react-phone-number-input/style.css";

import { UserProfile } from "../../components/auth/firestore/getUserDetails";
import { User } from "firebase/auth";
import MUITextFieldAdapter from "../../components/form/MUITextFieldAdapter";

import { sendCode, checkCode } from "../../api/wallet/phoneVerify";

interface Props {
  user: User | null;
  userData: UserProfile;
  setUserData: React.Dispatch<React.SetStateAction<UserProfile>>;
}

export const PhoneField: React.FC<Props> = ({ userData, setUserData }) => {
  const [phone, setPhone] = useState<E164Number | undefined>();
  const [editing, setEditing] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState<string | null>(null);

  /* pre-fill */
  useEffect(
    () => setPhone(userData?.phone as E164Number | undefined),
    [userData]
  );

  /* send OTP */
  const sendOtp = async () => {
    if (!phone) return;
    try {
      await sendCode(phone);
      setOtpOpen(true);
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    }
  };

  /* confirm OTP */
  const verifyOtp = async () => {
    if (!phone) return;
    try {
      await checkCode(phone, otp);
      setUserData((p) => ({ ...p, phone } as UserProfile)); // update UI
      setEditing(false);
      setOtpOpen(false);
      setOtp("");
    } catch (e: any) {
      setErr(e.message || "Incorrect code, try again.");
    }
  };

  return (
    <Box>
      <PhoneInput
        countries={["US", "CA"]}
        defaultCountry="CA"
        international
        value={phone}
        onChange={(v) => setPhone(v as E164Number | undefined)}
        inputComponent={MUITextFieldAdapter as any}
      />

      <Button
        fullWidth
        variant="contained"
        sx={{ mt: 2, fontWeight: "bold" }}
        onClick={() => (editing ? sendOtp() : setEditing(true))}
      >
        {editing ? "Send Code" : "Edit Phone"}
      </Button>

      <div id="recaptcha-container" style={{ display: "none" }} />

      <Dialog open={otpOpen} onClose={() => setOtpOpen(false)}>
        <DialogContent>
          <MUITextFieldAdapter
            label="Enter OTP"
            value={otp}
            onChange={(v) => setOtp(v ?? "")}
          />
          {err && <p style={{ color: "red" }}>{err}</p>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtpOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={otp.length !== 6}
            onClick={verifyOtp}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
